import type {
  NotificationReachResponse,
  QuietNotificationOrgsResponse,
} from '@nadoutong/contracts';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers around the two #1947 reads on `/v1/ops/notifications`.
 *
 * Both are gated on `ops-admin` at ops-api, same as the announce POST next
 * door; a support-only session surfaces 403 FORBIDDEN through `OpsApiError`.
 *
 * WHAT THESE NUMBERS MEAN is settled in
 * `packages/contracts/src/internal-notification-reach.ts` and every renderer
 * has to keep it: they are a REACHABILITY LOWER BOUND — "at least one member
 * of this org called the notification endpoint after the announcement was
 * written" — never read, delivery, or receipt.
 */

const REACH_PATH = '/v1/ops/notifications/reach';
const QUIET_ORGS_PATH = '/v1/ops/notifications/quiet-orgs';

export interface QuietNotificationOrgsQuery {
  readonly days?: number;
  readonly limit?: number;
  readonly offset?: number;
}

export async function getNotificationReach(
  fingerprints: readonly string[],
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<NotificationReachResponse> {
  // Comma-joined into one parameter rather than repeated keys: ops-api
  // forwards a whitelisted query by `searchParams.get`, which keeps only the
  // first occurrence of a repeated key — the extra fingerprints would vanish
  // with no error anywhere.
  const params = new URLSearchParams({ fingerprints: fingerprints.join(',') });
  return opsApiRequest<NotificationReachResponse>(
    `${REACH_PATH}?${params.toString()}`,
    { signal },
    fetchImpl,
  );
}

export async function listQuietNotificationOrgs(
  query: QuietNotificationOrgsQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<QuietNotificationOrgsResponse> {
  const params = new URLSearchParams();
  if (query.days !== undefined) params.set('days', query.days.toString());
  if (query.limit !== undefined) params.set('limit', query.limit.toString());
  if (query.offset !== undefined) params.set('offset', query.offset.toString());
  const serialized = params.toString();
  return opsApiRequest<QuietNotificationOrgsResponse>(
    `${QUIET_ORGS_PATH}${serialized.length > 0 ? `?${serialized}` : ''}`,
    { signal },
    fetchImpl,
  );
}
